import type { ButtonHTMLAttributes, Ref } from "react";

import { cn } from "../../utils";
import type { LoadingSize, LoadingType } from "../Loading";
import { Loading } from "../Loading";

export const buttonVariants = [
  "primary",
  "secondary",
  "alternative",
  "link",
  "peak",
  "action",
] as const;

export const buttonSizes = ["sm", "md", "lg", "xl"] as const;

export type ButtonSize = (typeof buttonSizes)[number];

export type ButtonVariant = (typeof buttonVariants)[number];

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>;
  size?: ButtonSize;
  variant?: ButtonVariant;
  isLoading?: boolean;
  loadingType?: LoadingType;
}

export const Button = ({
  size = "md",
  type = "button",
  variant = "primary",
  loadingType = "spinner",
  isLoading,
  className,
  ref,
  ...props
}: ButtonProps) => {
  const isAction = variant === "action";

  let loadingSize: LoadingSize = size === "sm" ? "sm" : "md";
  if (isAction && size === "sm") {
    loadingSize = "xs";
  }

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "ui:flex ui:cursor-pointer ui:items-center ui:justify-center ui:rounded-sm ui:font-grotesk ui:transition-all ui:enabled:active:scale-95 ui:disabled:cursor-not-allowed ui:disabled:opacity-50",
        {
          "ui:bg-[linear-gradient(91deg,#FFF_-31.93%,#3550FC_-31.92%,#9054FF_101.4%)] ui:text-white ui:shadow-[0px_2px_6px_0px_rgba(112,131,255,0.70)] ui:hover:opacity-85 ui:disabled:bg-black/10 ui:disabled:bg-none ui:disabled:text-textSecondary ui:disabled:shadow-none ui:dark:disabled:bg-white/20 ui:dark:disabled:text-white":
            variant === "primary",
          "ui:shadow-[0px_8px_14px_0px_rgba(130,130,130,0.10)_inset] ui:dark:shadow-[0px_8px_14px_0px_rgba(130,130,130,0.10)_inset,0px_2px_8px_0px_rgba(47,47,47,0.50)_inset]":
            variant === "secondary",
          "ui:bg-buttonHover ui:text-textPrimary ui:enabled:hover:shadow-[0px_2px_8px_0px_rgba(47,47,47,0.24)_inset] ui:dark:bg-buttonFill ui:enabled:dark:hover:bg-[#282828]":
            variant === "secondary" || variant === "alternative",
          "ui:font-sans ui:text-brandBlue ui:hover:text-primary ui:disabled:text-textSecondary":
            variant === "link",
          "ui:!h-[30px] ui:!min-h-[30px] ui:border ui:border-transparent ui:bg-primary/10 ui:font-sans ui:font-bold ui:text-primary ui:enabled:hover:border-primary":
            variant === "peak",
          "ui:bg-primary ui:font-medium ui:whitespace-nowrap ui:text-white ui:hover:opacity-85":
            isAction,
        },
        {
          "ui:h-8 ui:min-h-8 ui:px-3":
            size === "sm" && !["link", "action"].includes(variant),
          "ui:h-4.5 ui:min-h-4.5 ui:min-w-15 ui:px-2 ui:text-[11px]":
            size === "sm" && isAction,
          "ui:h-10 ui:min-h-10 ui:px-4": size === "md" && variant !== "link",
          "ui:h-12 ui:min-h-12 ui:px-6": size === "lg" && variant !== "link",
          "ui:h-13.5 ui:min-h-13.5 ui:px-6":
            size === "xl" && variant !== "link",
        },
        {
          "ui:text-xs": size === "sm" && !isAction,
        },
        className,
      )}
      {...props}
      disabled={isLoading || props.disabled}
    >
      {isLoading ? (
        <Loading size={loadingSize} type={loadingType} />
      ) : (
        props.children
      )}
    </button>
  );
};
